import useApiKeyCreationLock from '../hooks/useApiKeyCreationLock';
import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, Routes, Route, Navigate, Link} from 'react-router-dom';
import Select from 'react-select';
import { getActionSelectStyles } from '../utility/selectStyles';
import {useDispatch, useSelector} from 'react-redux';
import axiosInstance from '../utility/axiosInstance';
import Modal from '../components/Modal';
import ConfirmationModal from '../components/ConfirmationModal';
import {FaChevronLeft, FaChevronRight, FaCreditCard, FaGlobe, FaKey, FaTrash, FaRegCopy, FaPlus, FaSyncAlt, FaDownload} from 'react-icons/fa';
import {useToast} from '../components/ToastContext';
// BillingDetailsUser removed - BillingForecast is used instead
import {budgetSaved, fetchBillingForecast, fetchBudgetState} from '../store/slices/billingSlice';
import {fetchBillingHistory, resetHistory} from '../store/slices/billingHistorySlice';
import { fetchSubscriptionInfo, cancelSubscription, reactivateSubscription, fetchPortalSession, resetCancelStatus } from '../store/slices/paymentSlice';
import {RootState, AppDispatch} from '../store/store';
import '../styles/UserPage.scss';
import BillingHistory from "../components/BillingHistory";
import BillingForecast from "../components/BillingForecast";
import DomainsPage from './DomainsPage';
import ApiKeyScopePicker from '../components/ApiKeyScopePicker';
import ScopeBadges from '../components/ScopeBadges';
import BudgetSettings from '../components/BudgetSettings';
import RegistrationBilling from '../components/RegistrationBilling';

const SetupBillingModal = React.lazy(() => import('../components/SetupBillingModal'));

const ITEMS_PER_PAGE = 10;
const DEFAULT_API_KEY_EXPIRY_DAYS = 90;
const DEFAULT_API_KEY_SCOPES = ['read'];

const formatAccountDate = (value: string | number | Date | null | undefined) => {
    if (!value) return 'Not available';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? 'Not available'
        : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const BILLING_TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'usage', label: 'Usage details' },
    { id: 'history', label: 'Past usage' },
    { id: 'payment', label: 'Invoices & payment' },
] as const;

type BillingTab = (typeof BILLING_TABS)[number]['id'];

const ApiKeysSection: React.FC = () => {
    const apiKeyLock = useApiKeyCreationLock();
    const {showToast} = useToast();
    const [apiKeys, setApiKeys] = useState<any[]>([]);
    const [generatedKey, setGeneratedKey] = useState<string | null>(null);
    const [hasAcknowledgedGeneratedKey, setHasAcknowledgedGeneratedKey] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isNameModalOpen, setIsNameModalOpen] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyExpiry, setNewKeyExpiry] = useState(String(DEFAULT_API_KEY_EXPIRY_DAYS));
    const [newKeyScopeTokens, setNewKeyScopeTokens] = useState<string[]>(DEFAULT_API_KEY_SCOPES);
    const [scopesValid, setScopesValid] = useState(true);
    const [loading, setLoading] = useState(false);
    const [kpPage, setKpPage] = useState(0);
    const [pendingAction, setPendingAction] = useState<{
        type: 'delete_key' | 'error';
        title: string;
        message: React.ReactNode;
        confirmText?: string;
        cancelText?: string;
        onConfirm: () => void;
        confirmType?: 'warning' | 'danger' | 'info';
    } | null>(null);

    useEffect(() => {
        fetchKeys();
    }, []);

    useEffect(() => {
        setKpPage(0);
    }, [apiKeys.length]);

    const totalPages = Math.ceil(apiKeys.length / ITEMS_PER_PAGE);
    const paginatedKeys = apiKeys.slice(kpPage * ITEMS_PER_PAGE, (kpPage + 1) * ITEMS_PER_PAGE);

    const fetchKeys = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/v1/auth/keys');
            setApiKeys(response.data);
        } catch (err) {
            console.error("Failed to fetch API keys", err);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateKey = () => {
        if (apiKeyLock) return;
        setNewKeyName('');
        setNewKeyExpiry(String(DEFAULT_API_KEY_EXPIRY_DAYS));
        setNewKeyScopeTokens(DEFAULT_API_KEY_SCOPES);
        setScopesValid(true);
        setIsNameModalOpen(true);
    };

    const performGenerateKey = async () => {
        if (apiKeyLock || !newKeyName.trim()) return;
        setIsNameModalOpen(false);
        setIsGenerating(true);
        try {
            const payload: any = { name: newKeyName, scopes: newKeyScopeTokens.length ? newKeyScopeTokens : DEFAULT_API_KEY_SCOPES };
            const days = parseInt(newKeyExpiry, 10);
            if (Number.isFinite(days) && days > 0) payload.expires_in_days = days;
            const response = await axiosInstance.post('/v1/auth/keys', payload);
            setGeneratedKey(response.data.key);
            setHasAcknowledgedGeneratedKey(false);
            showToast('API key generated successfully!', 'success');
            fetchKeys();
        } catch (err) {
            showToast('Failed to generate API key', 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDeleteKey = (id: number, name: string) => {
        setPendingAction({
            type: 'delete_key',
            title: 'Delete API Key',
            message: `Are you sure you want to delete API key "${name}"? This action cannot be undone.`,
            confirmText: 'Yes, Delete',
            cancelText: 'Cancel',
            confirmType: 'danger',
            onConfirm: async () => {
                try {
                    await axiosInstance.delete(`/v1/auth/keys/${id}`);
                    setApiKeys(prev => prev.filter(k => k.id !== id));
                    setPendingAction(null);
                    showToast('API key deleted successfully!', 'success');
                } catch (err) {
                    showToast('Failed to delete API key', 'error');
                    setPendingAction(null);
                }
            }
        });
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast('API key copied to clipboard!', 'success');
            return true;
        } catch {
            showToast('Could not copy the API key. Select it and copy it manually.', 'error');
            return false;
        }
    };

    const selectStyles = {
        menuPortal: (base: any) => ({...base, zIndex: 9999}),
        control: (base: any, state: any) => ({
            ...base,
            minHeight: '44px',
            borderColor: state.isFocused ? 'var(--focus-ring)' : 'var(--border)',
            boxShadow: state.isFocused ? '0 0 0 3px var(--accent-subtle)' : 'none',
            '&:hover': {
                borderColor: 'var(--border-strong)',
            },
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused ? 'var(--surface-selected)' : 'var(--surface-card)',
            color: 'var(--text-primary)',
        }),
        indicatorSeparator: () => ({display: 'none'}),
        dropdownIndicator: (base: any) => ({
            ...base,
            color: 'var(--text-muted)',
            '&:hover': {
                color: 'var(--text-primary)',
            },
        }),
    };

    return (
        <div className="user-page">
            <header className="account-page-header">
                <h1>API keys</h1>
                <p>{apiKeyLock || 'Scoped access for scripts and integrations. Revoke any time.'}</p>
                {apiKeyLock && <Link to="/mcp">Use MCP with your Redu login</Link>}
            </header>

            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table">
                        <caption className="visually-hidden">API keys</caption>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Scopes</th>
                            <th>Created At</th>
                            <th>Last Used</th>
                            <th>Expires</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="loading-state">
                                    <FaSyncAlt className="spin"/> Loading API keys...
                                </td>
                            </tr>
                        ) : paginatedKeys.length > 0 ? (
                            paginatedKeys.map((key) => (
                                <tr key={key.id}>
                                    <td className="keypair-name" style={{padding: '32px'}}>{key.name}</td>
                                    <td style={{padding: '16px'}}><ScopeBadges scopes={key.scopes}/></td>
                                    <td style={{padding: '16px'}}>{formatAccountDate(key.created_at)}</td>
                                    <td style={{padding: '16px'}}>{key.last_used_at ? formatAccountDate(key.last_used_at) : 'Never'}</td>
                                    <td style={{padding: '16px'}}>{key.expires_at ? formatAccountDate(key.expires_at) : 'Never'}</td>
                                    <td style={{padding: '16px'}}>
                                        <div style={{width: '140px'}}>
                                            <Select
                                                options={[
                                                    {value: 'delete', label: 'Delete'}
                                                ]}
                                                onChange={(option) => {
                                                    if (option && option.value === 'delete') {
                                                        handleDeleteKey(key.id, key.name);
                                                    }
                                                }}
                                                placeholder="Actions"
                                                isSearchable={false}
                                                menuPortalTarget={document.body}
                                                styles={getActionSelectStyles()}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="empty-state">
                                    <div className="resource-empty-state">
                                        <strong>No API keys yet</strong>
                                        <span>Create keys for scripts and integrations.</span>
                                        <button type="button" disabled={!!apiKeyLock} title={apiKeyLock || undefined} onClick={handleGenerateKey}>Generate API key</button>
                                    </div>
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                            <button
                                onClick={() => setKpPage(p => Math.max(0, p - 1))}
                                disabled={kpPage === 0}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: kpPage === 0 ? 'not-allowed' : 'pointer', opacity: kpPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >← Prev</button>
                            <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {kpPage * ITEMS_PER_PAGE + 1}-{Math.min((kpPage + 1) * ITEMS_PER_PAGE, apiKeys.length)} of {apiKeys.length}
                            </span>
                            <button
                                onClick={() => setKpPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={kpPage >= totalPages - 1}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: kpPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: kpPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >Next →</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="mobile-view">
                {loading ? (
                    <div className="loading-state">
                        <FaSyncAlt className="spin"/>
                        <p>Loading API keys...</p>
                    </div>
                ) : (
                    <div className="cards-container">
                        {paginatedKeys.length > 0 ? (
                            paginatedKeys.map((key) => (
                                <div key={key.id} className="keypair-card">
                                    <div className="card-header" style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                                        <h3 className="card-title">{key.name}</h3>
                                        <div style={{width: '100%', marginTop: '12px'}}>
                                            <Select
                                                options={[
                                                    {value: 'delete', label: 'Delete'}
                                                ]}
                                                onChange={(option) => {
                                                    if (option && option.value === 'delete') {
                                                        handleDeleteKey(key.id, key.name);
                                                    }
                                                }}
                                                placeholder="Actions"
                                                isSearchable={false}
                                                menuPortalTarget={document.body}
                                                styles={getActionSelectStyles()}
                                            />
                                        </div>
                                    </div>
                                    <div className="card-content">
                                        <div className="fingerprint">
                                            <span className="label">Scopes:</span>
                                            <span className="value"><ScopeBadges scopes={key.scopes}/></span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Created:</span>
                                            <span className="value">{formatAccountDate(key.created_at)}</span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Last Used:</span>
                                            <span className="value">{key.last_used_at ? formatAccountDate(key.last_used_at) : 'Never'}</span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Expires:</span>
                                            <span className="value">{key.expires_at ? formatAccountDate(key.expires_at) : 'Never'}</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="resource-empty-state">
                                    <strong>No API keys yet</strong>
                                    <span>Create keys for scripts and integrations.</span>
                                    <button type="button" disabled={!!apiKeyLock} title={apiKeyLock || undefined} onClick={handleGenerateKey}>Generate API key</button>
                                </div>
                            </div>
                        )}
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                <button
                                    onClick={() => setKpPage(p => Math.max(0, p - 1))}
                                    disabled={kpPage === 0}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: kpPage === 0 ? 'not-allowed' : 'pointer', opacity: kpPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >← Prev</button>
                                <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                    {kpPage * ITEMS_PER_PAGE + 1}-{Math.min((kpPage + 1) * ITEMS_PER_PAGE, apiKeys.length)} of {apiKeys.length}
                                </span>
                                <button
                                    onClick={() => setKpPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={kpPage >= totalPages - 1}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: kpPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: kpPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >Next →</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <button
                className="create-button"
                onClick={handleGenerateKey}
                disabled={isGenerating || !!apiKeyLock}
                title={apiKeyLock || "Generate new API key"}
            >
                <FaPlus/>
                <span>{isGenerating ? 'Generating...' : 'Generate Key'}</span>
            </button>

            <Modal
                isOpen={isNameModalOpen && !apiKeyLock}
                onClose={() => setIsNameModalOpen(false)}
                title={apiKeyLock || "Generate new API key"}
            >
                <div className="create-form">
                    <div className="form-group">
                        <label htmlFor="api-key-name">Key name</label>
                        <input
                            id="api-key-name"
                            type="text"
                            value={newKeyName}
                            onChange={(e) => setNewKeyName(e.target.value)}
                            placeholder="e.g. My Application"
                            autoFocus
                            aria-describedby="api-key-name-help"
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                background: 'var(--surface-input)',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                fontSize: '1rem',
                            }}
                        />
                        <p id="api-key-name-help" className="form-help">Use a name that tells you where this key is used.</p>
                    </div>
                    <div className="form-group">
                        <label>Permissions</label>
                        <p className="form-help">Start read-only. Grant write or delete access only when the client needs it.</p>
                        <ApiKeyScopePicker
                            key={isNameModalOpen ? 'open' : 'closed'}
                            defaultPreset="read"
                            onChange={(scopes, valid) => {
                                setNewKeyScopeTokens(scopes);
                                setScopesValid(valid);
                            }}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="api-key-expiry">Expires in days</label>
                        <input
                            id="api-key-expiry"
                            type="number"
                            min="1"
                            max="365"
                            value={newKeyExpiry}
                            onChange={(e) => setNewKeyExpiry(e.target.value)}
                            placeholder={String(DEFAULT_API_KEY_EXPIRY_DAYS)}
                            aria-describedby="api-key-expiry-help"
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                background: 'var(--surface-input)',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                fontSize: '1rem',
                            }}
                        />
                        <p id="api-key-expiry-help" className="form-help">Keys expire after {DEFAULT_API_KEY_EXPIRY_DAYS} days by default. Clear this only for a key you will actively manage.</p>
                    </div>
                    <div className="modal-actions">
                        <button className="cancel-button" onClick={() => setIsNameModalOpen(false)}>
                            Cancel
                        </button>
                        <button className="submit-button" onClick={performGenerateKey} disabled={!newKeyName.trim() || !scopesValid || isGenerating}>
                            {isGenerating ? 'Generating...' : 'Generate'}
                        </button>
                    </div>
                </div>
            </Modal>

            {generatedKey && (
                <Modal
                    isOpen={!!generatedKey}
                    onClose={() => undefined}
                    title="API Key Generated"
                    closeOnEscape={false}
                    showCloseButton={false}
                >
                    <div className="create-form">
                        <p className="help-text" style={{marginBottom: '15px', color: '#ef4444', fontWeight: 'bold'}}>
                            Copy this key now. It will not be shown again.
                        </p>
                        <div className="form-group">
                            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                <code style={{
                                    flex: 1,
                                    padding: '10px',
                                    background: 'var(--surface-alt)',
                                    color: 'var(--text-primary)',
                                    borderRadius: '5px',
                                    wordBreak: 'break-all',
                                    border: '1px solid var(--border)'
                                }}>
                                    {generatedKey}
                                </code>
                                <button
                                    className="btn btn-secondary"
                                    onClick={async () => {
                                        if (await copyToClipboard(generatedKey)) setHasAcknowledgedGeneratedKey(true);
                                    }}
                                    title="Copy to clipboard"
                                    style={{padding: '10px'}}
                                >
                                    <FaRegCopy/>
                                </button>
                            </div>
                        </div>
                        <label className="secret-key-acknowledgement">
                            <input
                                type="checkbox"
                                checked={hasAcknowledgedGeneratedKey}
                                onChange={(event) => setHasAcknowledgedGeneratedKey(event.target.checked)}
                            />
                            <span>I have copied or deliberately discarded this key. I understand it cannot be shown again.</span>
                        </label>
                        <div className="modal-actions">
                            <button className="submit-button" type="button" disabled={!hasAcknowledgedGeneratedKey} onClick={() => setGeneratedKey(null)}>
                                Done
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {pendingAction && (
                <ConfirmationModal
                    isOpen={!!pendingAction}
                    onClose={() => setPendingAction(null)}
                    onConfirm={pendingAction.onConfirm}
                    title={pendingAction.title}
                    message={pendingAction.message}
                    confirmText={pendingAction.confirmText}
                    cancelText={pendingAction.cancelText}
                    type={pendingAction.confirmType}
                />
            )}
        </div>
    );
};

export const BillingSection: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const navigate = useNavigate();
    const { forecast, status: forecastStatus, budget: budgetState } = useSelector((state: RootState) => state.billingForecast);
    const { page, hasMore, status: historyStatus } = useSelector((state: RootState) => state.billingHistory);
    const { subscription, status: paymentStatus, cancelStatus, error: paymentError } = useSelector((state: RootState) => state.payment);
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState<BillingTab>('overview');
    const billingTabRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const [isExporting, setIsExporting] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [focusBudget, setFocusBudget] = useState(false);
    const legacyBilling=!!forecast && (forecast.policy_version==null||forecast.policy_version===1);
    const registrationBilling=forecast?.policy_version===2||forecast?.policy_version===3;

    useEffect(() => {
        dispatch(fetchBillingForecast());
        dispatch(fetchSubscriptionInfo());
        const query = new URLSearchParams(window.location.search);
        if (query.get('is_failed')) {
            showToast('Payment was cancelled or failed', 'error');
            window.history.replaceState({}, '', window.location.pathname);
        }
        if (query.get('session_id')) {
            showToast('Checkout completed successfully!', 'success');
            window.history.replaceState({}, '', window.location.pathname);
            dispatch(fetchSubscriptionInfo());
        }
        // Reset and fetch first page on mount
        dispatch(resetHistory());
        dispatch(fetchBillingHistory(1));
    }, [dispatch, showToast]);

    // ?setup-billing=1 (Connect card) and ?setup-budget=1 (Set budget), from the top banner and the home checklist. Read
    // from the router on every change of the search, not once at mount: the banner is on this page too, and its link to
    // the page already open only changes the search. Cleared through the router, so the same button works again
    // (BUDGET_MANDATORY_PLAN_2026-10-28 section 5.6).
    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const setupBilling = query.get('setup-billing') === '1';
        const setupBudget = query.get('setup-budget') === '1';
        if (!setupBilling && !setupBudget) return;
        if (setupBilling) {
            setActiveTab('payment');
            setIsSetupModalOpen(true);
        }
        if (setupBudget) {
            setActiveTab('overview');
            setFocusBudget(true);
        }
        navigate(location.pathname, { replace: true });
    }, [location.search, location.pathname, navigate]);

    useEffect(() => {
        if (cancelStatus === 'succeeded') {
            showToast('Action completed successfully', 'success');
            dispatch(resetCancelStatus());
            dispatch(fetchSubscriptionInfo());
        } else if (cancelStatus === 'failed') {
            showToast(paymentError || 'Action failed', 'error');
            dispatch(resetCancelStatus());
        }
    }, [cancelStatus, paymentError, dispatch, showToast]);

    const loadMore = () => {
        if (hasMore && historyStatus !== 'loading') {
            dispatch(fetchBillingHistory(page));
        }
    };

    const handleExportCsv = async () => {
        setIsExporting(true);
        try {
            const response = await axiosInstance.get('/v1/billing/export', { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'billing-usage.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            showToast('Failed to export billing data', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleCancelClick = () => {
        if(legacyBilling)setIsCancelModalOpen(true);
    };

    const confirmCancel = () => {
        if(legacyBilling)dispatch(cancelSubscription());
        setIsCancelModalOpen(false);
    };

    const handleReactivate = () => {
        if(legacyBilling)dispatch(reactivateSubscription());
    };

    const handleSubscribe = () => {
        setIsSetupModalOpen(true);
    };

    const activateBillingTab = (tab: BillingTab) => {
        setActiveTab(tab);
    };

    const handleBillingTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        let nextIndex: number | null = null;

        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                nextIndex = (index + 1) % BILLING_TABS.length;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                nextIndex = (index - 1 + BILLING_TABS.length) % BILLING_TABS.length;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = BILLING_TABS.length - 1;
                break;
            default:
                return;
        }

        if (nextIndex === null) return;
        event.preventDefault();
        const nextTab = BILLING_TABS[nextIndex];
        activateBillingTab(nextTab.id);
        billingTabRefs.current[nextIndex]?.focus();
    };

    const handleManagePaymentMethod = async () => {
        setIsProcessing(true);
        try {
            const action = await dispatch(fetchPortalSession());
            if (fetchPortalSession.fulfilled.match(action)) {
                window.location.href = action.payload;
            } else {
                showToast('Failed to open payment management', 'error');
            }
        } catch (err) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const renderPaymentPanel = () => (
        <section className="forecast-card billing-payment-panel">
            <div className="billing-section-heading">
                <div>
                    <h2>Invoices & payment</h2>
                    <p>Subscription status, next payment date, card management, and Stripe invoices.</p>
                </div>
                {subscription?.customerId && (
                    <button
                        className="billing-secondary-action"
                        onClick={handleManagePaymentMethod}
                        disabled={isProcessing}
                    >
                        {isProcessing ? 'Opening...' : 'Open billing portal'}
                    </button>
                )}
            </div>

            {paymentStatus === 'loading' && !subscription && (
                <div className="billing-inline-loading">
                    <FaSyncAlt className="spin" />
                    <span>Loading subscription details...</span>
                </div>
            )}

            {subscription && (
                <div className="subscription-info billing-payment-grid">
                    {subscription.paymentRecovery && !['recovered','deleted'].includes(subscription.paymentRecovery.phase) && (
                        <div className="billing-payment-row" role="status">
                            <span>Payment recovery</span>
                            <div>
                                <strong>{subscription.paymentRecovery.phase}</strong>
                                {subscription.paymentRecovery.nextRetryAt && <p>Next Stripe retry: {new Date(subscription.paymentRecovery.nextRetryAt).toLocaleString()}</p>}
                                {subscription.paymentRecovery.deletionAt && <p>Account and resource deletion: {new Date(subscription.paymentRecovery.deletionAt).toLocaleString()} ({Intl.DateTimeFormat().resolvedOptions().timeZone}). Settle all overdue invoices before this deadline.</p>}
                                <button className="billing-primary-action" onClick={handleManagePaymentMethod} disabled={isProcessing}>Pay overdue invoices</button>
                            </div>
                        </div>
                    )}
                    <div className="billing-payment-row">
                        <span>Status</span>
                        <div>
                            <strong className={`status-badge ${subscription.status}`}>{subscription.status}</strong>
                            {subscription.cancelAtPeriodEnd && <small>Pending cancellation</small>}
                        </div>
                    </div>
                    <div className="billing-payment-row">
                        <span>Next payment</span>
                        <strong>{subscription.nextPaymentDue ? formatAccountDate(subscription.nextPaymentDue) : 'Not scheduled'}</strong>
                    </div>
                    <div className="billing-payment-row">
                        <span>Invoices</span>
                        <strong>{subscription.customerId ? 'Available in Stripe portal' : 'No billing customer yet'}</strong>
                    </div>

                    <div className="billing-actions-row">
                        {legacyBilling && (subscription.status === 'active' || subscription.status === 'trialing') && !subscription.cancelAtPeriodEnd && (
                            <button
                                className="billing-danger-action"
                                onClick={handleCancelClick}
                                disabled={cancelStatus === 'loading' || isProcessing}
                            >
                                {cancelStatus === 'loading' ? 'Processing...' : 'Cancel subscription'}
                            </button>
                        )}

                        {legacyBilling && subscription.cancelAtPeriodEnd && (subscription.status === 'active' || subscription.status === 'trialing') && (
                            <button
                                className="billing-primary-action"
                                onClick={handleReactivate}
                                disabled={cancelStatus === 'loading' || isProcessing}
                            >
                                {cancelStatus === 'loading' ? 'Processing...' : 'Reactivate subscription'}
                            </button>
                        )}

                        {(subscription.status === 'none' || subscription.status === 'canceled') && (
                            <button className="billing-primary-action" onClick={handleSubscribe}>
                                Set up billing
                            </button>
                        )}

                        {subscription.customerId && (
                            <button
                                className="billing-secondary-action"
                                onClick={handleManagePaymentMethod}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Opening...' : 'Manage payment method'}
                            </button>
                        )}
                        {registrationBilling && <p>Manage your plan from <button className="billing-secondary-action" onClick={()=>setActiveTab('overview')}>Billing overview</button>. To end a monthly commitment, schedule a change to {forecast?.policy_version===3?'Regular':'Professional'} for your next renewal. Paid usage can continue; set a budget or delete paid resources to control it.</p>}
                    </div>
                </div>
            )}

            {paymentStatus === 'failed' && (
                <div className="error-message">
                    {paymentError || 'Failed to load subscription details'}
                </div>
            )}
        </section>
    );

    return (
        <div className="user-page">
            <div className="billing-section-content unified-billing">
                <header className="account-page-header">
                    <h1>Billing</h1>
                    <p>Track current usage, credits, payment details, and past charges.</p>
                </header>

                <div className="billing-tabs" role="tablist" aria-label="Billing sections">
                    {BILLING_TABS.map(({ id, label }, index) => (
                        <button
                            type="button"
                            key={id}
                            id={`billing-tab-${id}`}
                            role="tab"
                            ref={(element) => { billingTabRefs.current[index] = element; }}
                            className={activeTab === id ? 'active' : ''}
                            aria-selected={activeTab === id}
                            aria-controls={`billing-panel-${id}`}
                            tabIndex={activeTab === id ? 0 : -1}
                            onClick={() => activateBillingTab(id)}
                            onKeyDown={(event) => handleBillingTabKeyDown(event, index)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {activeTab === 'overview' && (
                    <section
                        className="forecast-card"
                        id="billing-panel-overview"
                        role="tabpanel"
                        aria-labelledby="billing-tab-overview"
                        tabIndex={0}
                    >
                            <div className="billing-section-heading">
                                <div>
                                    <h2>Billing overview</h2>
                                </div>
                            </div>
                            {/* A plan account gets the same budget card, through the plan API (Milos, 2026-11-01). */}
                            <RegistrationBilling renderBudget={(planContext) => (
                                <BudgetSettings
                                    plan={planContext}
                                    autoFocus={focusBudget}
                                    onFocused={() => setFocusBudget(false)}
                                    required
                                    onSaved={(saved) => dispatch(budgetSaved(saved))}
                                    // No credit band: a plan budget counts spending after credits, so credit is never part of it.
                                    creditBalance={null}
                                    forecastLoaded={forecastStatus === 'succeeded' && !!forecast}
                                    tier={forecast?.tier ?? null}
                                    forecastTotal={forecast?.summary?.forecasted_total_this_month ?? null}
                                />
                            )}>
                            {forecastStatus === 'loading' && !forecast && <div className="skeleton-loader" />}
                            {forecast && <BillingForecast forecast={forecast} nextPaymentDue={subscription?.nextPaymentDue ?? null} view="summary" />}
                            <BudgetSettings
                                autoFocus={focusBudget}
                                onFocused={() => setFocusBudget(false)}
                                required={budgetState?.required === true}
                                onSaved={(saved) => dispatch(budgetSaved(saved))}
                                creditBalance={forecast?.credits_for_period ?? null}
                                forecastLoaded={forecastStatus === 'succeeded' && !!forecast}
                                tier={forecast?.tier ?? null}
                                forecastTotal={forecast?.summary?.forecasted_total_this_month ?? null}
                            />
                            </RegistrationBilling>

                    </section>
                )}

                {activeTab === 'usage' && (
                    <section
                        className="forecast-card"
                        id="billing-panel-usage"
                        role="tabpanel"
                        aria-labelledby="billing-tab-usage"
                        tabIndex={0}
                    >
                        <div className="billing-section-heading">
                            <div>
                                <h2>Usage details</h2>
                                <p>Resource-level metering for this billing period.</p>
                            </div>
                        </div>
                        {forecastStatus === 'loading' && !forecast && <div className="skeleton-loader" />}
                        {forecast && <BillingForecast forecast={forecast} nextPaymentDue={subscription?.nextPaymentDue ?? null} detailsDefaultOpen view="usage" />}
                    </section>
                )}

                {activeTab === 'history' && (
                    <section
                        className="history-container"
                        id="billing-panel-history"
                        role="tabpanel"
                        aria-labelledby="billing-tab-history"
                        tabIndex={0}
                    >
                        <div className="billing-section-heading">
                            <div>
                                <h2>Past usage</h2>
                                <p>Monthly usage records. Invoices are available from the billing portal.</p>
                            </div>
                            <button
                                className="billing-secondary-action"
                                onClick={handleExportCsv}
                                disabled={isExporting}
                            >
                                {isExporting ? <FaSyncAlt className="spin" /> : <FaDownload />}
                                {isExporting ? 'Exporting...' : 'Download CSV'}
                            </button>
                        </div>

                        <BillingHistory />

                        {hasMore && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                                <button
                                    className="load-more-button"
                                    onClick={loadMore}
                                    disabled={historyStatus === 'loading'}
                                >
                                    {historyStatus === 'loading' ? (
                                        <><FaSyncAlt className="spin" /> Loading...</>
                                    ) : (
                                        'Load Older Records'
                                    )}
                                </button>
                            </div>
                        )}
                    </section>
                )}

                {activeTab === 'payment' && (
                    <div
                        id="billing-panel-payment"
                        role="tabpanel"
                        aria-labelledby="billing-tab-payment"
                        tabIndex={0}
                    >
                        {renderPaymentPanel()}
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={isCancelModalOpen && legacyBilling}
                onClose={() => setIsCancelModalOpen(false)}
                onConfirm={confirmCancel}
                title="Cancel Subscription"
                message="Are you sure you want to cancel your subscription? You will still have access until the end of the current billing period."
                confirmText="Yes, Cancel"
                cancelText="No, Keep it"
                type="danger"
            />

            {isSetupModalOpen && (
                <React.Suspense fallback={<div className="billing-inline-loading">Loading secure billing form...</div>}>
                    <SetupBillingModal
                        onClose={() => setIsSetupModalOpen(false)}
                        onSuccess={() => {
                            dispatch(fetchBillingForecast());
                            dispatch(fetchSubscriptionInfo());
                            // The card form saved a budget with the card: the banner reads the new state.
                            dispatch(fetchBudgetState());
                            showToast('Billing is set up. Any eligible credits have been applied to your account.', 'success');
                        }}
                    />
                </React.Suspense>
            )}
        </div>
    );
};

const UserPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isSidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
    const subcategories = [
        { label: 'API keys', route: 'api-keys', icon: FaKey },
        { label: 'Billing', route: 'billing', icon: FaCreditCard },
        { label: 'Domains', route: 'domains', icon: FaGlobe },
    ];

    return (
        <div className={`account-shell${isSidebarCollapsed ? ' account-shell--collapsed' : ''}`}>
            <aside className="account-sidebar" aria-label="Account navigation">
                <div className="account-sidebar__heading">
                    <span className="account-sidebar__eyebrow">Account</span>
                    <button
                        type="button"
                        className="account-sidebar__toggle"
                        onClick={() => setSidebarCollapsed(collapsed => !collapsed)}
                        aria-label={isSidebarCollapsed ? 'Expand account navigation' : 'Collapse account navigation'}
                    >
                        {isSidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                    </button>
                </div>
                <nav className="account-sidebar__nav">
                    {subcategories.map(({ label, route, icon }) => {
                        const isActive = location.pathname.includes(route) || (route === 'api-keys' && (location.pathname === '/category/user' || location.pathname === '/category/user/'));
                        return (
                            <button
                                type="button"
                                key={route}
                                className={`account-sidebar__item${isActive ? ' is-active' : ''}`}
                                onClick={() => navigate(`/category/user/${route}`)}
                                aria-current={isActive ? 'page' : undefined}
                                title={isSidebarCollapsed ? label : undefined}
                            >
                                {React.createElement(icon)}
                                <span>{label}</span>
                            </button>
                        );
                    })}
                </nav>
            </aside>

            <main className="account-content">
                <Routes>
                    <Route path="api-keys" element={<ApiKeysSection/>}/>
                    <Route path="billing" element={<BillingSection/>}/>
                    <Route path="payment" element={<Navigate to={`../billing${location.search}`} replace/>}/>
                    <Route path="domains" element={<DomainsPage/>}/>
                    <Route path="/" element={<Navigate to="api-keys" replace/>}/>
                </Routes>
            </main>
        </div>
    );
};

export default UserPage;
